import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';

import useAuth from 'src/hooks/useAuth';
import Iconify from 'src/components/Iconify';
import GenericTable from 'src/components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from 'src/components/table/GenericTable.design';
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Badge } from 'src/@/components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'src/@/components/ui/select';

import { transformStatusTemplateWhatsApp } from 'src/utils/meta.utils';
import actionListaCanaisPorCliente from 'src/actions/clientes/actionListaCanaisPorCliente';
import v1_actionMessengerTemplates from 'src/actions/v1/meta/messenger/v1_actionMessengerTemplates';
import v1_actionMessengerObterTemplate from 'src/actions/v1/meta/messenger/v1_actionMessengerObterTemplate';
import v1_actionMessengerDeletarTemplate from 'src/actions/v1/meta/messenger/v1_actionMessengerDeletarTemplate';
import v1_actionMessengerSincronizarTemplates from 'src/actions/v1/meta/messenger/v1_actionMessengerSincronizarTemplates';
import v1_actionMessengerCapacidades from 'src/actions/v1/meta/messenger/v1_actionMessengerCapacidades';
import v1_actionMessengerReativarWebhook from 'src/actions/v1/meta/messenger/v1_actionMessengerReativarWebhook';

import { TEMPLATES_MESSENGER_DESIGN as D } from './TemplatesMessenger.design';
import TemplatesMessengerDrawer from './TemplatesMessengerDrawer';
import EnviarTesteDialog from './EnviarTesteDialog';
import { statusBadgeClasses, statusDotClasses } from './templatesMessenger.utils';

export default function TemplatesMessengerPage() {
    const { user, cliente } = useAuth();
    const { enqueueSnackbar } = useSnackbar();

    const clienteId = cliente?._id || user?.clienteId;

    const [canais, setCanais] = useState([]);
    // O valor do seletor é o canal._id (e não cliente_id como no WhatsApp: em
    // Messenger o cliente pode ter várias Páginas, então só o _id identifica).
    const [canalId, setCanalId] = useState('');
    const [lista, setLista] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sincronizando, setSincronizando] = useState(false);
    const [reativandoWebhook, setReativandoWebhook] = useState(false);
    const [capacidades, setCapacidades] = useState(null);
    const [statusFiltro, setStatusFiltro] = useState('all');
    const [pesquisa, setPesquisa] = useState('');

    const [drawerModo, setDrawerModo] = useState(null); // 'criar' | 'biblioteca' | 'ver'
    const [templateSelecionado, setTemplateSelecionado] = useState(null);
    const [dialogTeste, setDialogTeste] = useState(false);

    const canal = useMemo(() => canais.find((c) => c._id === canalId), [canais, canalId]);

    // Identidade da Página para o preview. Vem de /capacidades porque a listagem de
    // canais não expõe `config` (que guarda o access token) — antes o preview mostrava
    // o nome do canal e nunca a foto.
    const pagina = useMemo(() => ({
        nome: capacidades?.pageName || canal?.nome || '',
        profile: capacidades?.pageProfile
    }), [capacidades, canal]);

    const buscarCanais = async () => {
        if (!clienteId) return;
        try {
            const response = await actionListaCanaisPorCliente(clienteId);
            const somenteMessenger = (response.data?.data || []).filter((c) => c.platform === 'messenger');
            setCanais(somenteMessenger);
            if (somenteMessenger.length > 0) setCanalId(somenteMessenger[0]._id);
        } catch (error) {
            console.error('Erro ao buscar canais:', error);
        }
    };

    const buscarTemplates = async () => {
        if (!canalId) return;
        setLoading(true);
        try {
            const response = await v1_actionMessengerTemplates(clienteId, { canalId });
            setLista(response.data?.data?.templates || []);
        } catch (error) {
            setLista([]);
            enqueueSnackbar(error.response?.data?.message || 'Erro ao buscar templates', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const buscarCapacidades = async () => {
        if (!canalId || !clienteId) {
            setCapacidades(null);
            return;
        }
        try {
            const response = await v1_actionMessengerCapacidades(clienteId, { canalId });
            setCapacidades(response.data?.data || null);
        } catch (error) {
            // Nao bloqueia a tela — sem diagnostico, esconde o banner permanente antigo
            setCapacidades(null);
        }
    };

    useEffect(() => { buscarCanais(); }, [clienteId]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        buscarTemplates();
        buscarCapacidades();
    }, [canalId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSincronizar = async () => {
        if (!canalId) return;
        setSincronizando(true);
        try {
            const response = await v1_actionMessengerSincronizarTemplates(clienteId, { canalId, userId: user?.id });
            enqueueSnackbar(response.data?.message || 'Templates sincronizados', { variant: 'success' });
            buscarTemplates();
            buscarCapacidades();
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || 'Erro ao sincronizar', { variant: 'error' });
        } finally {
            setSincronizando(false);
        }
    };

    const handleReativarWebhook = async () => {
        if (!canalId) return;
        setReativandoWebhook(true);
        try {
            const response = await v1_actionMessengerReativarWebhook(clienteId, { canalId });
            setCapacidades(response.data?.data || null);
            enqueueSnackbar(response.data?.message || 'Webhook atualizado', {
                variant: response.data?.data?.hasTemplateStatusWebhook ? 'success' : 'warning'
            });
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || 'Erro ao reassinar webhook', { variant: 'error' });
            buscarCapacidades();
        } finally {
            setReativandoWebhook(false);
        }
    };

    const handleVisualizar = async (template) => {
        setTemplateSelecionado(template);
        setDrawerModo('ver');

        // Recarrega para renovar o header_url da mídia (a URL da CDN da Meta expira)
        try {
            const response = await v1_actionMessengerObterTemplate(clienteId, template._id);
            if (response.data?.data?.template) setTemplateSelecionado(response.data.data.template);
        } catch (error) {
            // O template da listagem já é suficiente para visualizar
        }
    };

    const handleExcluir = async (template) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Excluir o template "${template.templateName || template.name}"?`)) return;

        try {
            const response = await v1_actionMessengerDeletarTemplate(clienteId, template._id);
            const aviso = response.data?.data?.metaSkipped;
            enqueueSnackbar(aviso || 'Template excluído', { variant: aviso ? 'warning' : 'success' });
            setDrawerModo(null);
            setTemplateSelecionado(null);
            buscarTemplates();
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || 'Erro ao excluir', { variant: 'error' });
        }
    };

    const listaFiltrada = useMemo(() => lista.filter((t) => {
        if (statusFiltro !== 'all' && t.status !== statusFiltro) return false;
        if (!pesquisa) return true;
        const termo = pesquisa.toLowerCase();
        return (t.templateName || '').toLowerCase().includes(termo)
            || (t.name || '').toLowerCase().includes(termo)
            || (t.description || '').toLowerCase().includes(termo);
    }), [lista, statusFiltro, pesquisa]);

    const stats = useMemo(() => ({
        total: lista.length,
        aprovados: lista.filter((t) => t.status === 'APPROVED').length,
        pendentes: lista.filter((t) => t.status === 'PENDING').length,
        rejeitados: lista.filter((t) => t.status === 'REJECTED').length
    }), [lista]);

    const abrirNovo = () => {
        setTemplateSelecionado(null);
        setDrawerModo('criar');
    };

    // Sem canal Messenger conectado não há o que gerenciar
    if (canais.length === 0) {
        return (
            <div className={D.page.container}>
                <div className="flex flex-col items-center justify-center py-20 px-6 bg-white rounded-xl border border-gray-100">
                    <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
                        <Iconify icon="logos:messenger" width={32} />
                    </div>
                    <p className="text-sm font-medium text-gray-900 mb-1">Nenhuma Página do Facebook conectada</p>
                    <p className="text-xs text-gray-500 text-center max-w-md">
                        Os templates de utility message pertencem a uma Página. Conecte um canal Messenger
                        em Configurações &gt; Canais para começar.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={D.page.container}>
                {/* Header */}
                <div className={D.page.header.container}>
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center border border-blue-100">
                            <Iconify icon="logos:messenger" width={28} />
                        </div>
                        <div>
                            <h1 className={D.page.header.title}>Templates Messenger</h1>
                            <p className={D.page.header.subtitle}>
                                Utility messages da Página — fale com o cliente fora da janela de 24h.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2.5">
                        <Button
                            variant="outline"
                            onClick={handleSincronizar}
                            disabled={sincronizando}
                            className={D.page.secondaryButton}
                        >
                            <Iconify
                                icon={sincronizando ? 'line-md:loading-twotone-loop' : 'eva:sync-outline'}
                                width={17}
                                className="mr-1.5"
                            />
                            <span className="hidden sm:inline">Sincronizar</span>
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => { setTemplateSelecionado(null); setDrawerModo('biblioteca'); }}
                            className={D.page.secondaryButton}
                        >
                            <Iconify icon="eva:book-outline" width={17} className="mr-1.5" />
                            <span className="hidden sm:inline">Biblioteca</span>
                        </Button>
                        <button onClick={abrirNovo} className={D.page.actionButton}>
                            <Iconify icon="eva:plus-outline" width={18} />
                            <span>Novo Template</span>
                        </button>
                    </div>
                </div>

                {/* Aviso so quando falta permissao ou webhook de verdade.
                    Reconectar Contas Sociais sozinho NAO resolve: o campo
                    message_template_status_update e assinado no canal/Pagina. */}
                {capacidades && (
                    capacidades.hasUtilityPermission === false
                    || capacidades.hasTemplateStatusWebhook === false
                ) && (
                    <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                        <Iconify icon="eva:alert-triangle-outline" width={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0 space-y-2">
                            <p className="text-sm font-semibold text-amber-900">
                                {capacidades.hasUtilityPermission === false
                                    ? 'Permissão pages_utility_messaging ausente'
                                    : capacidades.hasTemplateStatusWebhook === false
                                        ? 'Webhook de aprovação de templates não assinado'
                                        : 'Configuração de utility messages incompleta'}
                            </p>
                            <p className="text-sm text-amber-800">
                                {capacidades.hasUtilityPermission === false ? (
                                    <>
                                        Utility messages exigem <span className="font-mono text-xs">pages_utility_messaging</span> aprovada
                                        no App Dashboard e concedida no login do canal Messenger em Canais
                                        (não em Contas Sociais / Anúncios Meta).
                                    </>
                                ) : (
                                    <>
                                        Sem o campo <span className="font-mono text-xs">message_template_status_update</span>,
                                        o status do template não atualiza sozinho após a criação. Use Sincronizar
                                        ou reassine o webhook desta Página.
                                    </>
                                )}
                            </p>
                            <div className="flex flex-wrap items-center gap-2 pt-1">
                                {capacidades.hasTemplateStatusWebhook === false && (
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        disabled={reativandoWebhook}
                                        onClick={handleReativarWebhook}
                                        className="h-8 text-xs border-amber-300 bg-white hover:bg-amber-50 text-amber-900"
                                    >
                                        <Iconify
                                            icon={reativandoWebhook ? 'line-md:loading-twotone-loop' : 'eva:wifi-outline'}
                                            width={14}
                                            className="mr-1.5"
                                        />
                                        {reativandoWebhook ? 'Reassinando…' : 'Reassinar webhook'}
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    disabled={sincronizando}
                                    onClick={handleSincronizar}
                                    className="h-8 text-xs border-amber-300 bg-white hover:bg-amber-50 text-amber-900"
                                >
                                    <Iconify icon="eva:sync-outline" width={14} className="mr-1.5" />
                                    Sincronizar status
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Stats */}
                <div className={D.stats.grid}>
                    {[
                        { valor: stats.total, label: 'Total', icone: 'eva:layers-outline', cor: 'bg-violet-50', texto: 'text-violet-600' },
                        { valor: stats.aprovados, label: 'Aprovados', icone: 'eva:checkmark-circle-2-outline', cor: 'bg-green-50', texto: 'text-green-600' },
                        { valor: stats.pendentes, label: 'Pendentes', icone: 'eva:clock-outline', cor: 'bg-amber-50', texto: 'text-amber-500' },
                        { valor: stats.rejeitados, label: 'Rejeitados', icone: 'eva:close-circle-outline', cor: 'bg-red-50', texto: 'text-red-500' }
                    ].map((s) => (
                        <div key={s.label} className={D.stats.card}>
                            <div className={`${D.stats.iconBox} ${s.cor}`}>
                                <Iconify icon={s.icone} width={20} className={s.texto} />
                            </div>
                            <div>
                                <p className={D.stats.value}>{s.valor}</p>
                                <p className={D.stats.label}>{s.label}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Loading */}
                {loading && (
                    <div className="w-full">
                        <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-[#7C3AED] rounded-full animate-[pulse_1.5s_ease-in-out_infinite]" style={{ width: '40%' }} />
                        </div>
                    </div>
                )}

                {/* Filtros — sem filtro de categoria: em Página tudo é UTILITY */}
                <div className={D.filters.grid}>
                    <div>
                        <Label className={D.filters.label}>Página</Label>
                        <Select value={canalId} onValueChange={setCanalId}>
                            <SelectTrigger className={D.filters.select}>
                                <SelectValue placeholder="Selecione a Página" />
                            </SelectTrigger>
                            <SelectContent className="z-[9999]" position="popper" sideOffset={4}>
                                {/* A listagem de canais não projeta `config` (guarda o token
                                    da Página), então aqui só existem nome e nome_cliente —
                                    o nome da Página vem de /capacidades. */}
                                {canais.map((c) => (
                                    <SelectItem key={c._id} value={c._id}>
                                        {c.nome}
                                        {c.nome_cliente && c.nome_cliente !== c.nome ? ` · ${c.nome_cliente}` : ''}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className={D.filters.label}>Status</Label>
                        <Select value={statusFiltro} onValueChange={setStatusFiltro}>
                            <SelectTrigger className={D.filters.select}>
                                <SelectValue placeholder="Todos os status" />
                            </SelectTrigger>
                            <SelectContent className="z-[9999]" position="popper" sideOffset={4}>
                                <SelectItem value="all">Todos os status</SelectItem>
                                <SelectItem value="APPROVED">Aprovado</SelectItem>
                                <SelectItem value="PENDING">Pendente</SelectItem>
                                <SelectItem value="REJECTED">Rejeitado</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className={D.filters.label}>Pesquisar</Label>
                        <div className="relative">
                            <Iconify icon="eva:search-outline" width={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <Input
                                placeholder="Buscar por nome ou descrição..."
                                value={pesquisa}
                                onChange={(e) => setPesquisa(e.target.value)}
                                className={D.filters.searchInput}
                            />
                        </div>
                    </div>
                </div>

                {/* Tabela */}
                <GenericTable
                    headLabels={[
                        { id: 'name', label: 'Template', className: 'flex-1 pl-4' },
                        { id: 'origem', label: 'Origem', className: 'w-32' },
                        { id: 'language', label: 'Idioma', className: 'w-24' },
                        { id: 'vars', label: 'Variáveis', className: 'w-28' },
                        { id: 'status', label: 'Status', className: 'w-32 text-center' },
                        { id: 'actions', label: '', className: 'w-24 text-right pr-4' }
                    ]}
                >
                    {listaFiltrada.map((template) => (
                        <div
                            key={template._id}
                            className={`${GENERIC_TABLE_DESIGN.row.container} cursor-pointer`}
                            onClick={() => handleVisualizar(template)}
                        >
                            <div className="flex-1 pl-4 min-w-[200px] pr-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                                        <Iconify icon="logos:messenger" width={18} />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                                            {template.templateName || template.name}
                                        </h3>
                                        <p className="text-[11px] text-gray-400 font-mono truncate">
                                            {template.name}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="w-32">
                                <Badge
                                    variant="outline"
                                    className={`text-xs font-medium ${
                                        template.source === 'library'
                                            ? 'text-blue-600 border-blue-200 bg-blue-50'
                                            : 'text-gray-600 border-gray-200 bg-gray-50'
                                    }`}
                                >
                                    {template.source === 'library' ? 'Biblioteca' : 'Própria'}
                                </Badge>
                            </div>

                            <div className="w-24">
                                <span className="text-[11px] text-gray-500 bg-gray-50 border border-gray-100 rounded-md px-2 py-0.5">
                                    {template.language || 'pt_BR'}
                                </span>
                            </div>

                            <div className="w-28 text-xs text-gray-500">
                                {template.parameter_format === 'NAMED' ? 'Nomeadas' : 'Posicionais'}
                            </div>

                            <div className="w-32 flex justify-center">
                                <Badge
                                    variant="outline"
                                    className={`text-xs font-medium flex items-center gap-1.5 ${statusBadgeClasses(template.status)}`}
                                >
                                    <span className={`w-1.5 h-1.5 rounded-full ${statusDotClasses(template.status)}`} />
                                    {transformStatusTemplateWhatsApp(template.status)}
                                </Badge>
                            </div>

                            <div className="w-24 flex justify-end pr-4 gap-1" onClick={(e) => e.stopPropagation()}>
                                <button
                                    onClick={() => handleVisualizar(template)}
                                    title="Visualizar"
                                    className="p-1.5 hover:bg-violet-50 text-gray-400 hover:text-violet-600 rounded-lg transition-colors"
                                >
                                    <Iconify icon="eva:eye-outline" width={18} />
                                </button>
                                <button
                                    onClick={() => handleExcluir(template)}
                                    title="Excluir"
                                    className="p-1.5 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg transition-colors"
                                >
                                    <Iconify icon="eva:trash-2-outline" width={18} />
                                </button>
                            </div>
                        </div>
                    ))}

                    {listaFiltrada.length === 0 && !loading && (
                        <div className="flex flex-col items-center justify-center py-16 px-6">
                            <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4">
                                <Iconify icon="eva:inbox-outline" width={32} className="text-gray-300" />
                            </div>
                            <p className="text-sm font-medium text-gray-900 mb-1">Nenhum template encontrado</p>
                            <p className="text-xs text-gray-500 mb-4">
                                {pesquisa || statusFiltro !== 'all'
                                    ? 'Tente outros filtros.'
                                    : 'Crie do zero ou clone um modelo pré-aprovado da Meta.'}
                            </p>
                            {!pesquisa && statusFiltro === 'all' && (
                                <button onClick={abrirNovo} className="text-sm text-[#7C3AED] font-medium hover:underline">
                                    Criar template
                                </button>
                            )}
                        </div>
                    )}
                </GenericTable>
            </div>

            {drawerModo && (
                <TemplatesMessengerDrawer
                    modo={drawerModo}
                    canal={canal}
                    pagina={pagina}
                    clienteId={clienteId}
                    userId={user?.id}
                    template={templateSelecionado}
                    onTrocarModo={setDrawerModo}
                    onFechar={() => { setDrawerModo(null); setTemplateSelecionado(null); }}
                    onSalvo={() => {
                        setDrawerModo(null);
                        setTemplateSelecionado(null);
                        enqueueSnackbar('Template enviado à Meta', { variant: 'success' });
                        buscarTemplates();
                    }}
                    onExcluir={handleExcluir}
                    onEnviarTeste={(template) => { setTemplateSelecionado(template); setDialogTeste(true); }}
                />
            )}

            <EnviarTesteDialog
                aberto={dialogTeste}
                template={templateSelecionado}
                canal={canal}
                pagina={pagina}
                clienteId={clienteId}
                onFechar={() => setDialogTeste(false)}
                onEnviado={() => {
                    setDialogTeste(false);
                    enqueueSnackbar('Mensagem enviada', { variant: 'success' });
                }}
            />
        </>
    );
}
